/**
 * C8 — the consent screen an AI tool sends a person to: which app, where it
 * will send access (its address, so a look-alike name cannot hide it), and
 * the one client it may read. Approve hands the app a one-time code; Deny
 * tells it no. Both leave SynAnalytics for the app's own address.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import type { Client } from '../../reports/reports-api.ts';
import { mcpApi } from './mcp-api.ts';

export function McpConsent(props: {
  organisationId: string;
  clients: Client[];
  query: string;
}): ReactNode {
  const params = Object.fromEntries(new URLSearchParams(props.query));
  const [app, setApp] = useState<{ app_name: string; redirect_host: string } | null>(null);
  const [clientId, setClientId] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    mcpApi
      .describe(props.organisationId, props.query)
      .then(setApp, (error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'This request is not valid.'),
      );
  }, [props.organisationId, props.query]);
  const approve = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    mcpApi
      .approve(props.organisationId, params, clientId)
      .then(({ redirect }) => window.location.assign(redirect))
      .catch((error: unknown) => {
        setBusy(false);
        setProblem(error instanceof Error ? error.message : 'The app could not be connected.');
      });
  };
  const deny = () => {
    const target = new URL(params.redirect_uri ?? '');
    target.searchParams.set('error', 'access_denied');
    if (params.state) target.searchParams.set('state', params.state);
    window.location.assign(target.toString());
  };
  if (problem && !app) return <p className="kp-problem">{problem}</p>;
  if (!app) return <p className="as-empty">Loading…</p>;
  return (
    <form className="card mcp-consent" onSubmit={approve}>
      <h2>Connect {app.app_name} to SynAnalytics?</h2>
      <p>
        <strong>{app.app_name}</strong> will be able to read one client’s data, dashboards, reports,
        KPIs and client details, as you. Access goes to <strong>{app.redirect_host}</strong>. Only
        continue if you started this from that app.
      </p>
      <label className="rs-field">
        Client
        <select value={clientId} onChange={(event) => setClientId(event.target.value)} required>
          <option value="">Choose a client…</option>
          {props.clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </select>
      </label>
      <p className="kp-faint">
        If you may edit reports, it can also make the changes your agency allows (it asks you
        first). Disconnect it any time on the MCP page.
      </p>
      {problem && <p className="kp-problem">{problem}</p>}
      <div className="kp-dialog-actions">
        <button type="button" className="btn secondary" onClick={deny} disabled={busy}>
          Deny
        </button>
        <button type="submit" className="btn" disabled={busy || !clientId}>
          Allow access
        </button>
      </div>
    </form>
  );
}
